import { Typography, useMediaQuery } from '@mui/material';
import classNames from 'classnames';
import { CSSProperties } from 'react';
import { TwContainer } from '../../typings/tw.types';
import { imageUrl } from '../../utils';

interface StepsBoxProps extends TwContainer {
  heading: string;
  subheading: string;
  describeImage: string;
  headingStyle?: CSSProperties;
  subHeadingStyle?: CSSProperties;
  subHeadingClass?: string;
  iconStyle?: CSSProperties;
  noWidth?: boolean;
  className?: string;
  alt?: string;
  description?: string;
  descriptionStyle?: CSSProperties;
  iconDimension?: number;
  highlighted?: boolean;
  onMouseEnter?: () => void;
}

const StepsBox = ({
  containerClasses,
  heading,
  subheading,
  describeImage,
  headingStyle = { fontSize: 48, fontWeight: 500, color: '#333333', lineHeight: 1.25, fontFamily: 'ABeeZee' },
  subHeadingStyle = { fontSize: 22, fontWeight: 500, color: '#333333', marginTop: 4, lineHeight: 1.15, fontFamily: 'ABeeZee' },
  subHeadingClass,
  iconStyle = { width: 52, height: 52 },
  noWidth,
  className,
  alt,
  description,
  descriptionStyle = { fontSize: 16, color: '#666666', lineHeight: 1.25, fontFamily: 'ABeeZee' },
  highlighted = false,
  onMouseEnter,
}: StepsBoxProps) => {
  const activeText = highlighted ? { color: '#FFFFFF' } : {};
  // The icons are gray SVGs loaded via <img>; brightness-0 + invert renders them white.
  const iconClasses = classNames('transition-[filter] duration-200', { 'brightness-0 invert': highlighted });

  const isLessThan1024 = useMediaQuery('(max-width: 1024px)');
  const responsiveHeadingStyle: CSSProperties = {
    ...headingStyle,
    ...activeText,
    fontSize: isLessThan1024 ? 38 : (typeof headingStyle.fontSize === 'number' ? headingStyle.fontSize : 48),
  };
  return (
    <div
      onMouseEnter={onMouseEnter}
      style={highlighted ? { backgroundColor: '#AEAB00', borderColor: '#000000' } : undefined}
      className={classNames(
        'relative h-full rounded-xl shadow-sm mdmax:!mx-0 flex flex-col p-5 md:p-6 lg:p-5 border-2 border-transparent transition-all duration-200 hover:z-10 hover:-translate-y-1 hover:scale-[1.04]',
        {
          'w-full sm:w-1/2 lg:w-1/3': !noWidth,
        },
        containerClasses,
      )}>


      {/* Heading row (number + icon): show on tablet and up (md and above) */}
      <div className="hidden md:flex justify-between items-center mb-2">
        <Typography variant="cardheading" className={className} style={responsiveHeadingStyle}>
          {heading}
        </Typography>
        <div style={iconStyle}>
          <img loading="lazy" className={iconClasses} src={imageUrl(describeImage)} alt={alt || ""} />
        </div>
      </div>
      
      {/* Subheading: on mobile show icon + title inline; on desktop show title only */}
      <div className="mb-1 min-h-[45px]">
        {/* Mobile (<md): title left, icon right */}
        <div className="md:hidden flex items-center justify-between">
          <Typography className={subHeadingClass} style={{ fontFamily: 'var(--fontPrimary)', ...subHeadingStyle, ...activeText }}>
            {subheading}
          </Typography>
          <div style={iconStyle}>
            <img loading="lazy" className={iconClasses} src={imageUrl(describeImage)} alt={alt || ""} />
          </div>
        </div>
        {/* Tablet & Desktop (>=md): title only */}
        <div className="hidden md:block">
          <Typography className={subHeadingClass} style={{ fontFamily: 'var(--fontPrimary)', ...subHeadingStyle, ...activeText }}>
            {subheading}
          </Typography>
        </div>
      </div>
      
      {/* Description */}
      <div className="flex-1 min-h-[24px]">
        <Typography style={{ fontFamily: 'var(--fontPrimary)', ...descriptionStyle, ...activeText }}>
          {description}
        </Typography>
      </div>
    </div>
  );
};

export default StepsBox;
